<template>
  <div>
    <v-expansion-panel-header class="location-panel-header">
      <v-row dense align="center">
        <span>
          {{ propLocation ? propLocation.name : '' }}
        </span>
        <v-spacer />
        <v-col cols="auto">
          <Icon
          iconColor="success"
          v-if="index === openPanel"
          icon="mdi-pencil"
          class="rotate"
          :tooltipText="$t('edit')"
          :small="false"
          @icon-clicked="editLocation"
          :disabled="!userAssignedClaim(UserClaims.GlobalAdmin)" />
        </v-col>

        <v-col cols="auto">
          <Icon
          iconColor="error"
          v-if="index === openPanel"
          icon="mdi-delete"
          :tooltipText="$t('delete')"
          :small="false"
           @icon-clicked="deleteLocationConfirm"
           :disabled="!userAssignedClaim(UserClaims.GlobalAdmin)" />
        </v-col>
      </v-row>
      <template #actions>
        <v-icon class="ml-2 location-panel-expansion-icon">mdi-chevron-down</v-icon>
      </template>
    </v-expansion-panel-header>

    <v-expansion-panel-content class="py-4">
      <v-row>
        <v-col cols="12">
          <v-tabs v-model="currentLocationTab">
            <v-tab :disabled="loading">
              <v-icon class="mr-1">mdi-account-box</v-icon>
              {{ $t('contact') }}
            </v-tab>

            <v-tab :disabled="loading">
              <v-icon class="mr-1">mdi-home</v-icon>
              {{ $t('configuration') }}
            </v-tab>

            <v-tab :disabled="loading">
              <v-icon class="mr-1">{{ dockageDeck.path }}</v-icon>
              {{ $t('decks') }}
            </v-tab>

            <v-tab :disabled="loading">
              <v-icon class="mr-1">{{ receiver.path }}</v-icon>
              {{ $t('receiver') }}
            </v-tab>

            <v-tab :disabled="loading">
              <v-icon class="mr-1">mdi-alert-decagram</v-icon>
              {{ $t('defects') }}
            </v-tab>
          </v-tabs>
        </v-col>

        <v-col v-if="loading" class="mt-6" justify="center" align="center">
          <v-progress-circular indeterminate color="primary" />
        </v-col>

        <v-col cols="12" v-else>
          <v-window v-model="currentLocationTab">
            <v-window-item>
              <LocationContactCards :location="location" />
            </v-window-item>

            <v-window-item>
              <v-row>
                <v-col cols="12">
                  <DefectConfiguration v-if="location.locationId !== -1" :location="location" @instructions-mutated="decksMutated"/>
                </v-col>
              </v-row>
            </v-window-item>

            <v-window-item>
              <v-row>
                <v-col cols="12">
                  <Decks :location="location" :decks="location.decks" :consumptionMode="location.consumptionMode"
                    @deck-mutated="decksMutated" />
                </v-col>
              </v-row>
            </v-window-item>

            <v-window-item>
              <v-row>
                <v-col cols="12">
                  <ReceiverSettings :locationId="propLocation.locationId"
                    :defectCollectionMode="propLocation.defectCollectionMode" />
                </v-col>
              </v-row>
            </v-window-item>

            <v-window-item>
              <v-row>
                <v-col cols="12">
                  <MultiDefects :location="location" :defects="defects" @defect-mutated="defectsMutated" />
                </v-col>
              </v-row>
            </v-window-item>
          </v-window>

        </v-col>
      </v-row>
    </v-expansion-panel-content>

    <Dialog :stateId="dialogId" maxWidth="80vw" @close="resetDialogs">
      <LocationForm v-if="locationForm" :propLocation="location" :businessEntityId="businessEntityId"
        @location-mutated="locationMutated" @close="resetDialogs" />

      <ConfirmDialog v-if="deleteDialog" :title="`Delete ${location.name}`" :body="$t('deleteLocationBody')"
        @confirm="confirmDeleteLocation" @cancel="resetDialogs" />
    </Dialog>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import { UserClaims } from '@/utils/Enumerations.js'
import { userAssignedClaim } from '@/utils/ClaimUtility.js'
import { uniqueDialogId } from '@/utils/componentHelpers'
import { dockageDeck, receiver } from '@/images/AngelSvgLibrary'
import { cloneDeep } from 'lodash'

const LocationTab = {
  Configuration: 0,
  Contact: 1,
  Decks: 2,
  Receiver: 3,
  Defects: 4
}

export default {
  name: 'LocationPanel',

  props: {
    locations: {
      type: Array,
      default: () => []
    },
    propLocation: {
      type: Object,
      default: () => { }
    },
    index: {
      type: Number,
      default: 0
    },
    openPanel: {
      type: Number,
      default: undefined
    },
    businessEntityId: {
      type: Number,
      default: undefined
    },
    entityTab: {
      type: Number,
      default: undefined
    },
    entityIndex: {
      type: Number,
      default: undefined
    }
  },

  components: {
    Decks: () => import('@/components/settings-components/decks/Decks.vue'),
    MultiDefects: () => import('@/components/defects/MultiDefects.vue'),
    Icon: () => import('@/components/helper/Icon.vue'),
    DefectConfiguration: () => import('@/components/settings-components/company-config/location/defect/DefectConfiguration.vue'),
    LocationForm: () => import('@/components/settings-components/company-config/location/LocationForm.vue'),
    ReceiverSettings: () => import('@/components/settings-components/company-config/location/receiver-settings/ReceiverSettings.vue'),
    Dialog: () => import('@/components/Dialog.vue'),
    ConfirmDialog: () => import('@/components/helper/ConfirmDialog.vue'),
    LocationContactCards: () => import('@/components/settings-components/company-config/location/LocationContactCards.vue')
  },

  data: () => ({
    dockageDeck,
    receiver,
    LocationTab,
    currentLocationTab: LocationTab.Configuration,
    dialogId: uniqueDialogId('location-panel'),
    location: {
      locationId: -1,
      name: '',
      mainContact: {},
      address: {},
      ticketContact: {},
      ticketNote: '',
      timeZoneOffset: -1,
      consumptionMode: 0,
      accountId: -1,
      segmentLength: null,
      collectLogCounts: false,
      decks: [],
      logLengths: []
    },
    locationForm: false,
    deleteDialog: false,
    defects: [],
    loading: false,
    UserClaims,
    receiverSettingsFetched: false,
    defectsFetched: false
  }),

  computed: {
    onReceiverTab () {
      return this.currentLocationTab === this.LocationTab.Receiver
    },

    onDefectsTab () {
      return this.currentLocationTab === this.LocationTab.Defects
    }
  },

  async created () {
    try {
      this.loading = true
      if (this.locations.length === 1) {
        await this.fetchLocationInformation()
      }
    } finally {
      this.loading = false
    }
  },

  watch: {
    openPanel: {
      async handler (val) {
        if (this.index === val && this.entityIndex === this.entityTab) {
          this.loading = true
          try {
            await this.fetchLocationInformation()
          } finally {
            this.loading = false
          }
        }
      }
    },

    entityTab: {
      async handler (val) {
        if (this.entityIndex === val && this.openPanel === this.index) {
          this.loading = true
          try {
            await this.fetchLocationInformation()
          } finally {
            this.loading = false
          }
        }
      }
    },

    async onReceiverTab (val) {
      if (val && !this.receiverSettingsFetched) {
        this.receiverSettingsFetched = true
        this.loading = true
        await this.receiverSettingsMutated()
        this.loading = false
      }
    },

    async onDefectsTab (val) {
      if (val && !this.defectsFetched) {
        this.defectsFetched = true
        this.loading = true
        await this.defectsMutated()
        this.loading = false
      }
    }
  },

  methods: {
    ...mapActions('locations', ['deleteLocation', 'fetchLocationWithId']),
    ...mapActions('defects', ['fetchDefectsForLocation']),
    ...mapActions('dialog', ['openOrUpdateDialog', 'closeDialogsAtOrAbove']),
    ...mapActions('receiver-settings', ['fetchReceiverSettings']),
    userAssignedClaim,

    async fetchLocationInformation () {
      const response = await this.fetchLocationWithId(this.propLocation.locationId)
      this.location = cloneDeep(response)
    },

    async defectsMutated () {
      if (this.location.locationId) {
        this.loading = true
        try {
          const response = await this.fetchDefectsForLocation(this.location.locationId)
          this.defects = cloneDeep(response)
        } finally {
          this.loading = false
        }
      }
    },

    async receiverSettingsMutated () {
      if (this.location.locationId) {
        this.loading = true
        try {
          await this.fetchReceiverSettings(this.location.locationId)
        } finally {
          this.loading = false
        }
      }
    },

    resetDialogs () {
      this.closeDialogsAtOrAbove(this.dialogId)
      this.locationForm = false
      this.deleteDialog = false
    },

    deleteLocationConfirm () {
      this.deleteDialog = true
      this.openOrUpdateDialog({ id: this.dialogId, width: '400', allowFullscreen: false })
    },

    async confirmDeleteLocation () {
      this.loading = true
      try {
        await this.deleteLocation(this.location.locationId)
        this.deleteDialog = false
        this.$emit('entity-mutated')
      } finally {
        this.loading = false
      }
    },

    editLocation () {
      this.locationForm = true
      this.openOrUpdateDialog({ id: this.dialogId, width: '80vw' })
    },

    async locationMutated () {
      this.loading = true
      try {
        const response = await this.fetchLocationWithId(this.propLocation.locationId)
        this.locationForm = false
        this.closeDialogsAtOrAbove(this.dialogId)
        this.location = cloneDeep(response)
      } finally {
        this.loading = false
      }
      this.$emit('entity-mutated')
    },

    async decksMutated () {
      this.loading = true
      try {
        const response = await this.fetchLocationWithId(this.propLocation.locationId)
        this.location = cloneDeep(response)
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style>
.location-panel-header.v-expansion-panel-header--active .v-icon.location-panel-expansion-icon {
  transform: rotate(180deg);
}
</style>
